import type { StackProps, SxProps } from '@mui/material';
import { Box } from '@mui/material';
import React, { createContext, useContext, type ReactNode } from 'react';

import { mergeSx } from '@/lib/utils.ts';

import { NumberCircle } from './NumberCircle.tsx';

const Context = createContext<{
  renderNumber: (props: { children: ReactNode }) => ReactNode;
  renderLabel: (props: { children: ReactNode }) => ReactNode;
}>(undefined!);

export type NumberedListProps = {
  renderNumber?: (props: { children: ReactNode }) => ReactNode;
  renderLabel?: (props: { children: ReactNode }) => ReactNode;
  sx?: SxProps;
  children?: ReactNode;
} & Pick<StackProps<'ol'>, 'direction'>;

export const NumberedList: React.FC<NumberedListProps> = ({
  renderNumber = (props) => <NumberCircle {...props} />,
  renderLabel = (props) => <p {...props} />,
  sx,
  children,
  ...props
}) => {
  return (
    <Context.Provider value={{ renderNumber, renderLabel }}>
      <Box
        component="ol"
        {...props}
        sx={mergeSx(
          {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 1,
            listStyle: 'none',
            margin: 0,
            padding: 0,
            counterReset: 'item',
          },
          sx,
        )}
      >
        {children}
      </Box>
    </Context.Provider>
  );
};

const counter = (
  <Box
    component="span"
    sx={{
      '&:before': {
        counterIncrement: 'item',
        content: 'counter(item)',
      },
    }}
  />
);

export type NumberedListItemProps = {
  children?: ReactNode;
};

export const NumberedListItem: React.FC<NumberedListItemProps> = ({ children }) => {
  const { renderNumber: Number, renderLabel: Label } = useContext(Context);

  return (
    <Box component="li" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Number>{counter}</Number>
      <Label>{children}</Label>
    </Box>
  );
};
